<script setup>
import { ref, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import MainLayout from '@/layout/main/MainLayout.vue';
import Button from 'primevue/button';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';
import ColorPicker from 'primevue/colorpicker';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import { useForm } from '@inertiajs/vue3';
import { useLoading } from '@/composables/useLoading';

defineOptions({
    layout: MainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    title: String,
});

// Color state for ColorPicker component
const selectedColor = ref('3B82F6');

// Form
const form = useForm({
    name: '',
    color: '#3B82F6',
});

// Watch for color changes and update form
const updateColor = (value) => {
    // ColorPicker returns the value without #, so we add it
    form.color = '#' + value;
};

// Functions
const submit = () => {
    showLoading();
    form.post(route('client.projects.store'), {
        onSuccess: () => {
            hideLoading();
            toast.add({ 
                severity: 'success', 
                summary: $t('Success'), 
                detail: $t('Project created successfully'), 
                life: 3000 
            });
        },
        onError: () => {
            hideLoading();
            toast.add({ 
                severity: 'error', 
                summary: $t('Error'), 
                detail: $t('Failed to create project'), 
                life: 3000 
            });
        }
    });
};

const cancel = () => {
    router.get(route('client.projects.index'));
};

const resetForm = () => {
    form.reset();
    form.color = '#3B82F6';
    selectedColor.value = '3B82F6';
};
</script>

<template>
    <Toast />
    
    <!-- Page Header -->
    <div class="surface-0 border-surface-200 border rounded-lg p-6 mb-4">
        <div class="flex items-center gap-3">
            <div class="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg">
                <i class="pi pi-folder-plus text-primary-600 text-2xl"></i>
            </div>
            <div>
                <h1 class="text-2xl font-bold text-surface-900">{{ $t('New Project') }}</h1>
                <p class="text-sm text-surface-600">{{ $t('Get started by filling in the information below to create your new project.') }}</p>
            </div>
        </div>
    </div>

    <!-- Form Card -->
    <Card>
        <template #content>
            <form @submit.prevent="submit" class="flex flex-col gap-6">
                <!-- Project Name -->
                <div class="flex flex-col gap-2">
                    <label for="name" class="font-semibold text-surface-900">
                        {{ $t('Project name') }} <span class="text-red-500">*</span>
                    </label>
                    <InputText 
                        id="name"
                        v-model="form.name" 
                        :placeholder="$t('Enter project name')"
                        class="w-full"
                        :disabled="form.processing"
                        :class="{ 'p-invalid': form.errors.name }"
                    />
                    <small v-if="form.errors.name" class="p-error">{{ form.errors.name }}</small>
                </div>

                <!-- Project Color -->
                <div class="flex flex-col gap-2">
                    <label for="color" class="font-semibold text-surface-900">
                        {{ $t('Color') }} <span class="text-red-500">*</span>
                    </label>
                    <div class="flex items-center gap-4">
                        <ColorPicker 
                            id="color"
                            v-model="selectedColor"
                            @update:modelValue="updateColor"
                            :disabled="form.processing"
                            format="hex"
                        />
                        <div class="flex items-center gap-2">
                            <span class="text-sm text-surface-600">{{ $t('Preview') }}:</span>
                            <div 
                                class="w-16 h-16 rounded-lg border-2 border-surface-300 flex items-center justify-center text-white font-semibold"
                                :style="{ backgroundColor: form.color }"
                            >
                                {{ form.name ? form.name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2) : 'PR' }}
                            </div>
                        </div>
                    </div>
                    <small v-if="form.errors.color" class="p-error">{{ form.errors.color }}</small>
                </div>

                <!-- Actions -->
                <div class="flex items-center justify-between pt-4 border-t border-surface-200">
                    <Button 
                        type="button"
                        :label="$t('Reset')" 
                        icon="pi pi-refresh"
                        text 
                        @click="resetForm"
                        :disabled="form.processing"
                    />
                    <div class="flex items-center gap-2">
                        <Button 
                            type="button"
                            :label="$t('Cancel')" 
                            severity="secondary"
                            @click="cancel"
                            :disabled="form.processing"
                        />
                        <Button 
                            type="submit"
                            :label="$t('Create Project')" 
                            icon="pi pi-check"
                            :loading="form.processing"
                            severity="primary"
                        />
                    </div>
                </div>
            </form>
        </template>
    </Card>

    <!-- Help Information -->
    <Card class="mt-4">
        <template #content>
            <div class="flex items-start gap-3">
                <i class="pi pi-info-circle text-primary-600 text-xl mt-1"></i>
                <div class="flex-1">
                    <h3 class="font-semibold text-surface-900 mb-2">{{ $t('About Projects') }}</h3>
                    <ul class="list-disc list-inside text-surface-700 space-y-1">
                        <li>{{ $t('Projects help you organize and group your website selections') }}</li>
                        <li>{{ $t('Each project can contain multiple websites') }}</li>
                        <li>{{ $t('Use colors to visually distinguish between different projects') }}</li>
                        <li>{{ $t('You can edit or delete projects at any time from the projects list') }}</li>
                    </ul>
                </div>
            </div>
        </template>
    </Card>
</template>